import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Users } from 'lucide-react';
import CTA from '../../components/CTA';

const PortadownWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Management Portadown | Emotional Eating Hypnotherapy County Armagh</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Portadown from Belfast clinic (30 miles). Address emotional eating, retail worker stress eating, busy schedule patterns. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Portadown, emotional eating Portadown, weight management County Armagh, stress eating Portadown, mindful eating Portadown" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/portadown-weight-management/" />
        <meta property="og:title" content="Weight Management Portadown | Emotional Eating Hypnotherapy County Armagh" />
        <meta property="og:description" content="Weight management hypnotherapy serving Portadown. Address emotional eating, stress eating patterns, sustainable lifestyle change." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Portadown",
            "description": "CNHC registered weight management hypnotherapy serving Portadown from Belfast clinic. Specializing in emotional eating, retail worker stress eating, and sustainable behavior change without diet culture.",
            "url": "https://www.sharonclare-hypnotherapy.com/portadown-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Portadown, Northern Ireland",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy in Portadown
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Address emotional eating and retail worker stress eating patterns with compassionate, evidence-based support
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">30 miles</span> from Portadown via M1 • <span className="font-semibold">40-45 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for Portadown residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management for Portadown
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Portadown community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, located approximately 30 miles northeast via the M1 motorway. The journey takes just 40-45 minutes from the Portadown area, providing accessible weight management support at a manageable distance that makes regular in-person sessions entirely practical for residents of this commercial hub.
              </p>
              <p>
                Weight struggles in Portadown often connect to the unique pressures of working in the retail and service economy. If you work at Rushmere Shopping Centre, High Street Mall, or other retail environments, you're dealing with customer-facing stress, irregular break patterns, standing on your feet all day, and the temptation of convenient food courts and coffee shops. When lunch breaks are short and unpredictable, when you're grabbing what you can between customers, when stress from difficult shoppers sends you reaching for comfort food during your break - these patterns aren't about willpower. They're understandable responses to the demanding pace of retail work.
              </p>
              <p>
                Solution-focused hypnotherapy for weight management works differently from diets. Instead of focusing on restriction, calorie counting, and food rules that eventually fail, we address why you eat when you're not physically hungry. By calming your stress response, building emotional resilience around workplace pressure, and changing your relationship with food at a subconscious level, you can achieve sustainable behavior change without the cycle of deprivation and rebound that characterizes most diets. My approach is compassionate, non-judgmental, and focused on long-term lifestyle change rather than unrealistic quick fixes or diet culture promises.
              </p>
              <p>
                Many Portadown residents work in retail, hospitality, or service roles that create specific eating challenges. Standing all day leads to fatigue eating - you're genuinely exhausted and your brain craves quick energy. Customer service stress triggers comfort food cravings - that chocolate bar in your bag becomes your stress relief after dealing with difficult customers. Irregular schedules and short breaks mean you grab convenient food rather than planning proper meals. The busy commercial environment of Portadown, with easy access to takeaways and fast food, makes emotional eating patterns even harder to resist without addressing the underlying stress.
              </p>
              <p>
                Hypnotherapy helps you develop healthier coping strategies that don't involve food. By addressing the stress of retail work, customer pressures, and the physical demands of being on your feet all day, we can break the automatic connection between workplace stress and eating. When your nervous system learns to stay calm despite difficult customers, when you have better tools for managing fatigue and work pressure, food loses its power as your primary stress relief mechanism. The changes happen at a subconscious level, so they feel natural rather than forced, creating sustainable weight management that works even when your job remains demanding.
              </p>
              <p>
                For Portadown clients unable to make the 40-45 minute drive to Belfast regularly, or who simply prefer the convenience of remote sessions, I also offer secure online hypnotherapy via video call. Online sessions provide the same professional, evidence-based treatment with the flexibility to attend from your home. This option has been particularly valuable for clients with irregular retail schedules, weekend work patterns, or those who want to fit sessions around unpredictable shift rotas. The therapeutic effectiveness is equivalent to in-person sessions while offering maximum convenience for busy retail workers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Emotional Eating */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Understanding Retail Worker Stress Eating
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As someone who has worked extensively with service sector employees throughout Northern Ireland, I understand the specific stressors that contribute to emotional eating patterns in Portadown's retail community. Customer-facing stress is relentless - dealing with complaints, managing difficult shoppers, maintaining professionalism when you're feeling overwhelmed, absorbing other people's frustration all day. When you finally get a break, that coffee and biscuit or chocolate bar becomes your reward and your stress relief, often eaten quickly standing up before you have to get back on the shop floor.
              </p>
              <p>
                The physical demands of retail work also drive eating patterns. Standing all day is genuinely exhausting, and fatigue triggers powerful cravings for quick energy from sugar and carbohydrates. Your body is depleted and your brain seeks the fastest fuel source - that's not a character flaw, it's basic physiology. The problem is that when you're eating to combat exhaustion rather than genuine hunger, and when those quick-energy foods spike and crash your blood sugar, you create a cycle of cravings and energy crashes that feels impossible to escape.
              </p>
              <p>
                Irregular break patterns in retail make healthy eating even more challenging. When you don't know exactly when you'll get your lunch break, when those breaks are often short and interrupted, when you're fitting food in around customer flow rather than eating at regular times, your body's natural hunger signals get disrupted. You might skip breakfast because you're rushing to work, grab something quick and convenient at lunch because there's no time for proper food, then come home exhausted and overeat in the evening because you're genuinely hungry and depleted from the day.
              </p>
              <p>
                The commercial environment in Portadown also contributes to eating patterns. Working near shopping center food courts, coffee chains, and takeaways means constant exposure to food cues and convenient options. The smell of fresh pastries, coffee shops on every corner, easy access to fast food - these environmental triggers make emotional eating harder to resist when you're already stressed and tired. It's not about willpower - it's about being surrounded by food designed to appeal to stressed, time-pressured workers exactly like you.
              </p>
              <p>
                What all these patterns have in common is that they're driven by stress and fatigue, not by physical hunger. Your subconscious mind has learned that food provides quick relief from difficult customers, exhaustion, or the accumulated pressure of retail work. The problem is that emotional eating creates its own cycle: work stress leads to eating, which leads to weight gain and guilt, which leads to more stress and shame, which leads to more eating. Breaking this cycle requires addressing the workplace stress at its root, not just trying to "eat less and move more."
              </p>
              <p>
                Hypnotherapy interrupts this cycle by teaching your nervous system to calm down despite customer pressure and work demands. When you have healthier ways to manage difficult emotions - quick stress relief techniques you can use on your break, better energy management strategies for long shifts, emotional resilience that doesn't depend on food - eating loses its power as your only coping mechanism. When your subconscious beliefs about food, work stress, and self-care shift from depletion and shame to balance and compassion, sustainable behavior change becomes possible even when your job remains demanding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How Hypnotherapy Helps with Weight Management
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Addresses Emotional Eating
                </h3>
                <p className="text-gray-700">
                  When customer stress, work pressure, or retail fatigue trigger eating, it's your subconscious mind running old patterns. Hypnotherapy accesses that subconscious level, breaking the connection between workplace stress and eating for comfort. You learn healthier ways to manage difficult customers, long shifts, and irregular schedules that don't involve food, creating lasting change rather than temporary willpower.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Calms Stress Response
                </h3>
                <p className="text-gray-700">
                  When your body is stuck in stress mode from customer-facing pressure, standing all day, or irregular work schedules, hunger hormones increase, cravings intensify, and your brain becomes hyper-focused on high-calorie foods. By teaching your nervous system to switch off the stress response even during busy shifts, hypnotherapy naturally reduces cravings and emotional eating urges without requiring constant willpower.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Users className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Sustainable Behavior Change
                </h3>
                <p className="text-gray-700">
                  Diets work short-term but fail long-term because they rely on willpower and restriction. Hypnotherapy creates lasting change by addressing the root causes of overeating - retail work stress, customer pressure, fatigue eating, and subconscious beliefs about food. The changes feel natural, not forced, making sustainable weight management genuinely achievable even when your work schedule remains irregular and demanding.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              My Approach to Weight Management
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 mb-4">
                I don't make promises about weight loss numbers, and I don't provide meal plans or diet advice. My focus is entirely on the psychological and emotional aspects of eating. You already know what healthy eating looks like - the problem isn't lack of information, it's that customer stress, work fatigue, and irregular schedules override that knowledge.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Instead of telling you what to eat or promising you'll lose a specific amount of weight, I work with you to understand why you eat when you're not physically hungry. What workplace triggers send you reaching for comfort food? How does customer pressure affect your eating patterns? What subconscious beliefs about stress relief and self-care are keeping you stuck? When we address these underlying drivers, your relationship with food changes naturally.
              </p>
              <p className="text-lg text-gray-700">
                This approach is particularly important for retail workers in Portadown because I understand that your challenges aren't just about "willpower" - they're about managing real customer stress, dealing with physical exhaustion from long shifts, navigating irregular schedules, and finding ways to care for yourself within the constraints of demanding service work. My approach is practical, compassionate, and focused on changes that work within your actual life circumstances.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                What to Expect from Sessions
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Initial Consultation</h4>
                  <p className="text-gray-700">
                    We'll explore your weight history, eating patterns, work stress triggers, retail environment, and goals. I'll explain how customer pressure and shift work drive eating behaviors, and how hypnotherapy works at a neurological level to create change. You'll also experience your first hypnotherapy session, leaving feeling calm and motivated.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ongoing Sessions</h4>
                  <p className="text-gray-700">
                    Most clients benefit from a package of 3 sessions, typically weekly or fortnightly depending on your shift rota. Each session builds on the last, gradually reprogramming your subconscious patterns around food, work stress, and emotional eating. You'll notice changes fairly quickly - reduced cravings during breaks, better awareness of hunger versus stress-eating, more control around food court temptations, improved energy despite long shifts, and a calmer relationship with food and your body.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Session Options for Portadown
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Single Session
                </h3>
                <p className="text-gray-700 mb-4">
                  One comprehensive weight management hypnotherapy session addressing your emotional eating patterns and work stress triggers.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Full assessment and consultation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Hypnotherapy session</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Take-home audio recording</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 border-2 border-accent-600">
                <div className="bg-accent-600 text-white text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">
                  MOST POPULAR
                </div>
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  3-Session Package
                </h3>
                <p className="text-gray-700 mb-4">
                  Comprehensive weight management programme for sustainable behavioral change and lasting results.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Three full hypnotherapy sessions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Progressive subconscious reprogramming</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Audio recordings for reinforcement</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Email support between sessions</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-xl p-6 border border-gray-200">
              <p className="text-gray-700 text-center">
                For retail workers in Portadown managing busy schedules, transparent pricing means no hidden costs or pressure to buy additional packages. Many clients find the 3-session package provides the best value for creating lasting change in eating patterns and stress management, with flexible scheduling to fit around shift rotas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Portadown
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Recommended)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M1 motorway northbound into Belfast from Portadown, then south along the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> 30 miles through Craigavon and Lisburn
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 40-45 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Public transport:</span> Metro services run along the Ormeau Road corridor, with Botanic and City Hospital the nearest rail stations
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Practical for Retail Workers</h3>
                <p className="text-gray-700">
                  At 30 miles via the M1 motorway, the journey from Portadown to Belfast is manageable for regular appointments, especially for those with days off during the week. The 40-45 minute drive is a reasonable investment for professional weight management support that addresses the root causes of retail work stress eating rather than just providing another temporary diet that will fail when work pressure intensifies.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer not to drive to Belfast, or working irregular shifts that make scheduling difficult? I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional treatment with the convenience of attending from your home in Portadown, eliminating travel time and fitting more easily around unpredictable retail rotas. This option is ideal for those working weekends, late shifts, or anyone who prefers the flexibility of online therapy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What County Armagh Clients Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in retail at Rushmere, I was constantly stress-eating on breaks - dealing with difficult customers all day, then rewarding myself with chocolate or coffee shop treats. Sharon helped me understand I was eating to cope with work stress, not because I was actually hungry. After the 3-session package, I've broken the automatic connection between customer stress and eating. I'm managing work pressure better and not turning to food every break anymore."
                </p>
                <p className="font-semibold text-gray-900">J.M., Craigavon Borough - Retail Work Stress Eating</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Standing all day in the shop leaves you absolutely exhausted, and I'd grab quick energy from sugary snacks and coffee constantly. Sharon's approach isn't about restricting yourself or following another diet - it's about understanding the stress and fatigue patterns driving your eating. The hypnotherapy helped me develop better energy management strategies and break the cycle of sugar crashes. My eating patterns are so much healthier now and I feel better overall."
                </p>
                <p className="font-semibold text-gray-900">A.H., County Armagh - Fatigue Eating Patterns</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working irregular shifts made healthy eating feel impossible - grabbing food whenever I could, no regular meal times, too tired to cook proper food after late shifts. Online sessions worked perfectly around my unpredictable rota. Sharon helped me develop a healthier relationship with food that actually works around shift work. The changes feel sustainable this time, not like restrictive diets that fall apart the first time you work a double shift."
                </p>
                <p className="font-semibold text-gray-900">R.D., Portadown - Shift Work Eating Challenges</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Portadown Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Portadown from the Belfast clinic?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 30 miles from Portadown via the M1 motorway. The journey typically takes 40-45 minutes by car in normal traffic conditions. The M1 is a high-quality dual carriageway that makes for a straightforward drive. For retail workers with days off during the week, this journey is genuinely practical for regular appointments - it's a reasonable distance for specialist support that addresses the root causes of stress eating rather than just providing another temporary diet.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can sessions be done online?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Online hypnotherapy sessions via video call are just as effective as in-person sessions. The hypnotherapy process works equally well through a screen, and many Portadown clients working irregular retail shifts find online sessions more convenient - no 40-minute drive to Belfast, easier to schedule around unpredictable rotas and weekend work, and you're in your own safe space at home. For retail workers juggling demanding schedules, online sessions often provide the consistency needed for successful weight management without travel time pressures.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for retail worker stress eating?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is particularly effective for retail work stress eating because it addresses the root cause - the stress response itself - rather than just trying to control food intake through willpower. When difficult customers trigger your stress response, when standing all day exhausts you, when irregular breaks disrupt your eating patterns, these create automatic emotional eating behaviors that run at a subconscious level. Hypnotherapy accesses that subconscious level, teaching your nervous system to stay calm during work stress, breaking the automatic connection between customer pressure and eating, and building healthier coping strategies that don't involve food.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does busy schedule affect eating patterns?
                </h3>
                <p className="text-gray-700">
                  Busy retail schedules create multiple challenges for healthy eating. Irregular break times mean you can't eat at predictable intervals, disrupting your body's natural hunger signals. Short breaks mean you grab convenient food quickly rather than eating proper meals. Being surrounded by food courts and coffee shops creates constant temptation when you're stressed and tired. Standing all day depletes your energy, triggering intense cravings for quick sugar hits. These aren't personal failures - they're predictable responses to the demanding pace of retail work. Hypnotherapy helps by reducing your stress response, improving your awareness of true hunger versus stress-eating, and developing practical strategies that work within the real constraints of retail work schedules.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What's the cost?
                </h3>
                <p className="text-gray-700">
                  You can book a single session or the 3-session package. The package provides better value and ensures commitment to the process - sustainable behavior change rarely happens in just one session. For retail workers in Portadown managing demanding schedules, transparent pricing means no hidden costs, no pressure to buy additional sessions, and no expensive meal plans to purchase. What you pay is what you get - professional hypnotherapy support to address emotional eating and create lasting change.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I do sessions on weekends?
                </h3>
                <p className="text-gray-700">
                  Clinic days and times vary, with early evening appointments available, which can work well for retail workers with weekday days off. However, I understand that retail schedules often include weekend work and irregular shift patterns. Online sessions via video call offer more flexibility for scheduling, allowing you to book appointments that fit your actual rota rather than trying to coordinate around travel to Belfast. Many retail workers find that combining occasional in-person sessions with regular online sessions provides the best balance of personal connection and practical flexibility around unpredictable work schedules.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default PortadownWeightManagement;
